import { Button, LinkButton } from '@/components/elements/forms/buttons';
import Label from '@/components/elements/Label/Label';
import MerchantLogo from '@/components/elements/MerchantLogo/MerchantLogo';
import Rating from '@/components/elements/redesign/Rating/Rating';
import { StatusIcon } from '@/components/elements/StatusIcon';
import Icon from '@/components/icons/Icon';
import { DealSearchItem } from '@/features/deals/types/dealSearch';
import { classnames, decodeUnicodeString } from '@/helpers';
import { getImagePreview } from '@/helpers/images';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { Fragment, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import * as sanitizeHtml from 'sanitize-html';
import CroLabel from '../../components/CroLabel';
import { DEAL_CONDITION } from '../../constants';
import { mapCroLabelProps } from '../../helpers';
import { DealDetailItem } from '../../types/dealDetails';
import { getPlacePageUrl } from './DealDetails.helpers';

type DealHeaderProps = { deal: DealDetailItem | null; loading: boolean; onReviewsClick?: () => void };

type CardWrapperProps<T extends React.ElementType = 'div'> = {
  as?: T;
  className?: string;
} & Omit<
  React.ComponentPropsWithoutRef<T>,
  keyof {
    as?: T;
    className?: string;
  }
>;

const baseTranslationKey = 'features.deals.pages.DealDetails.components' as const;

const cardClassNames = 'p-xl lg:p-3xl gap-lg bg-surface_l2 flex flex-col drop-shadow-sm rounded-lg';
const cardHeaderClassNames = 'text-fg_primary text-h-m font-bold';

export const CardWrapper = <T extends React.ElementType = 'div'>({ as, children, className }: CardWrapperProps<T>) => {
  const Component = as || 'div';
  return <Component className={classnames(cardClassNames, className)}>{children}</Component>;
};

export const DealHeader = ({ deal, loading, onReviewsClick }: DealHeaderProps) => {
  const { isMobileView } = useMobileView();
  if (loading || !deal) {
    return (
      <div className="space-y-md">
        {/* Title skeleton */}
        <div className="space-y-sm px-md lg:px-0">
          <div style={{ height: 44 }} className="bg-bg_secondary_bold w-2/4 animate-pulse rounded" />
        </div>
      </div>
    );
  }

  const croLabels = (deal?.croLabels || []).map(mapCroLabelProps).filter(Boolean);
  const discountLabels = deal?.discountLabels || [];
  const singleDiscountLabel = discountLabels.length === 1 ? discountLabels[0] : null;
  const multipleDiscountLabels = !singleDiscountLabel && discountLabels.length > 1 ? discountLabels : null;

  return (
    <div>
      <div className="space-y-md">
        {/* Title and Merchant Info */}
        <div className="space-y-sm">
          <h1 className="text-fg_primary mb-md line-clamp-[2] text-ellipsis whitespace-pre-wrap text-2xl font-bold md:text-3xl">
            {deal.name}
          </h1>

          {!isMobileView && croLabels?.length > 0 && (
            <div className="gap-md flex flex-wrap">
              {croLabels.map((label, index) => (
                <CroLabel key={index} {...label} showBackground />
              ))}
            </div>
          )}

          {/* show up to desktop breakpoint */}
          {isMobileView && (
            <div className="space-x-md flex w-full items-center">
              <div className="space-y-xs flex-1">
                <span className="text-fg_secondary text-l line-clamp-[2] text-ellipsis whitespace-pre-wrap">
                  {deal.place?.name}
                  {deal.place?.address?.city ? ` ${_t('in')} ${deal.place?.address?.city}` : ''}
                </span>

                {/* Rating */}
                {(deal.place.rating?.count ?? 0) > 0 && (
                  <div className="flex items-center">
                    <Rating
                      onReviewsClick={onReviewsClick}
                      avgRating={deal.place?.rating?.score}
                      ratingCount={deal.place?.rating?.count}
                      size="md"
                      showCount
                    />
                  </div>
                )}
              </div>
              {singleDiscountLabel && (
                <Label
                  size="lg"
                  variant={singleDiscountLabel.type}
                  className="whitespace-nowrap"
                  label={singleDiscountLabel.label}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {isMobileView && multipleDiscountLabels && (
        <div className="gap-xs mt-xl flex flex-row">
          {multipleDiscountLabels.map((label, index) => (
            <Label key={index} size="lg" variant={label.type} className="whitespace-nowrap" label={label.label} />
          ))}
        </div>
      )}
    </div>
  );
};

type DealCTAContainerProps = {
  deal: DealDetailItem | null;
  loading?: boolean;
  onBookClick?: () => void;
  onReviewsClick?: () => void;
};

export const DealCTAContainer = ({ deal, loading, onBookClick, onReviewsClick }: DealCTAContainerProps) => {
  const history = useHistory();
  const { isMobileView } = useMobileView();

  if (loading || !deal) {
    return (
      <CardWrapper>
        <div className="space-y-xs">
          <div className="bg-bg_secondary animate-pulse rounded-md" style={{ width: 80, height: 80 }} />
          <div className="bg-bg_secondary h-10 w-28 animate-pulse rounded" />
          <div className="bg-bg_secondary h-4 w-16 animate-pulse rounded" />
        </div>
        <div className="bg-bg_secondary h-12 w-full animate-pulse rounded-lg" />
        <div className="bg-bg_secondary mx-auto h-4 w-36 animate-pulse rounded" />
      </CardWrapper>
    );
  }

  const summaryItemRowClassNames = classnames(
    'border-border_regular gap-sm pt-sm space-x-md flex w-full justify-between border-t',
    'last:pb-sm last:border-b',
  );

  const renderSummaryRow = (
    props:
      | {
          type: 'service';
          item: DealDetailItem['services'][0];
        }
      | {
          type: 'product';
          item: DealDetailItem['products'][0];
        },
  ) => {
    const { item, type } = props;
    const isDiscounted = Boolean(item.originalPriceLabel);
    return (
      <div className={classnames(summaryItemRowClassNames)}>
        <span className="space-y-xs flex flex-col">
          <span className="text-fg_primary text-l line-clamp-[2] ">{item.name}</span>
          {type === 'service' && <span className="text-fg_secondary text-m">{item.durationLabel}</span>}
          {type === 'product' && <span className="text-fg_secondary text-m">{_t('Product')}</span>}
        </span>
        <span className="space-y-xs flex flex-shrink-0 flex-col">
          <span className={classnames('text-l', isDiscounted ? 'text-fg_highlight' : 'text-fg_secondary')}>
            {item.priceLabel}
          </span>
          <span className="text-m text-fg_secondary line-through">{item.originalPriceLabel}</span>
        </span>
      </div>
    );
  };

  const renderDiscountLabels = (labels: DealDetailItem['discountLabels']) => {
    return labels.map((label, index) => (
      <Label key={index} className="w-max" variant={label.type} label={label.label} />
    ));
  };

  const placePageUrl = getPlacePageUrl(deal);

  const merchantLogoWrapper = (component: ReactNode) => {
    if (!placePageUrl) return component;
    return (
      <button onClick={() => history.push({ pathname: placePageUrl })} className="cursor-pointer">
        {component}
      </button>
    );
  };

  return (
    <CardWrapper className="gap-xl lg:p-xl">
      <div className="flex flex-row items-start justify-between">
        {merchantLogoWrapper(<MerchantLogo size="lg" alt="" imgUrl={deal.place.profileImageURL} />)}

        {!isMobileView && deal.discountLabels?.length > 0 && (
          <div className="gap-xs flex flex-col items-end">{renderDiscountLabels(deal.discountLabels)}</div>
        )}
      </div>

      <div className="space-y-md">
        <h3 className="text-h-m text-fg_primary font-bold">{deal.place.name}</h3>
        {deal.place?.rating?.count > 0 && (
          <Rating
            showOneStar
            avgRating={deal.place.rating?.score ?? 0}
            ratingCount={deal.place.rating?.count ?? 0}
            size="md"
            showCount
            onReviewsClick={onReviewsClick}
          />
        )}

        <div className="space-y-md">
          {(deal.services || []).map((item) => (
            <Fragment key={`service-${item.id}`}>{renderSummaryRow({ type: 'service', item })}</Fragment>
          ))}
          {(deal.products || []).map((item, index) => (
            <Fragment key={`product-${index}`}>{renderSummaryRow({ type: 'product', item })}</Fragment>
          ))}
        </div>
      </div>

      <div className="bg-bg_secondary_neutral py-md px-lg text-fg_primary flex items-center rounded-sm text-xl font-semibold">
        <span className="flex-1">{_t('total')}</span>
        <span>{deal.priceLabel ?? deal.originalPriceLabel}</span>
      </div>

      <div>
        {onBookClick && (
          <Button variant="primary" size="lg" block onClick={onBookClick}>
            {_t('bookTime')}
          </Button>
        )}

        {placePageUrl && (
          <LinkButton variant="link" size="md" block to={{ pathname: placePageUrl, hash: 'about' }}>
            {_t('readMoreAboutCompany')}
          </LinkButton>
        )}
      </div>
    </CardWrapper>
  );
};

type AboutSectionProps = {
  deal: DealSearchItem | null;
  loading?: boolean;
};

export const AboutSection = ({ deal, loading }: AboutSectionProps) => {
  if (loading || !deal) {
    return (
      <CardWrapper as="section">
        <div className="bg-bg_secondary h-6 w-28 animate-pulse rounded" />
        <div className="space-y-sm">
          <div className="bg-bg_secondary h-4 w-full animate-pulse rounded" />
          <div className="bg-bg_secondary h-4 w-full animate-pulse rounded" />
          <div className="bg-bg_secondary h-4 w-3/4 animate-pulse rounded" />
        </div>
      </CardWrapper>
    );
  }

  return (
    <CardWrapper as="section">
      <h2 className={classnames(cardHeaderClassNames)}>{_t(`${baseTranslationKey}.AboutSection.title`)}</h2>
      <div className="text-fg_secondary text-l">
        {deal.description && (
          <div
            id="description"
            className="whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: deal.description }}
          />
        )}
      </div>
    </CardWrapper>
  );
};

type ConditionsSectionProps = {
  deal?: DealSearchItem | null;
  loading?: boolean;
};

export const ConditionsSection = ({ deal, loading }: ConditionsSectionProps) => {
  if (loading || !deal) {
    return (
      <CardWrapper as="section">
        <div className="bg-bg_secondary h-6 w-20 animate-pulse rounded" />
        <div className="space-y-md">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="gap-sm flex">
              <div className="bg-bg_secondary h-5 w-5 animate-pulse rounded" />
              <div className="space-y-xs flex-1">
                <div className="bg-bg_secondary h-4 w-24 animate-pulse rounded" />
                <div className="bg-bg_secondary h-4 w-40 animate-pulse rounded" />
              </div>
            </div>
          ))}
        </div>
      </CardWrapper>
    );
  }

  const conditions = (() => {
    let output = [];

    if (deal.publicationStart && deal.publicationEnd) {
      output.push(DEAL_CONDITION.VALID_DATES(deal));
    }

    if (deal.targetGroup && deal.targetGroup !== 'all') {
      output.push(DEAL_CONDITION.TARGET_GROUP(deal));
    }

    if (deal.maxCountCustomer) {
      output.push(DEAL_CONDITION.MAX_PURCHASES(deal));
    }

    return output;
  })();

  const sanitizedConditions = sanitizeHtml(decodeUnicodeString(deal.conditions ?? ''), {
    allowedTags: [],
    allowedAttributes: {},
  });

  return (
    <CardWrapper as="section" className="@container">
      <h2 className={classnames(cardHeaderClassNames)}>{_t(`${baseTranslationKey}.ConditionsSection.title`)}</h2>

      <div>
        {conditions.map((condition, index) => (
          <div key={index} className="gap-sm border-border_regular py-sm flex [&:not(:last-of-type)]:border-b">
            <div className="grid flex-shrink-0 items-center">
              <Icon variant={condition.icon} size="sm" color="fg_secondary" />
            </div>
            <div className="@sm:flex-row @sm:items-center @sm:gap-sm gap-3xs flex flex-1 flex-col">
              <div className="text-fg_secondary text-s @sm:text-l @sm:flex-1">{condition.label}</div>
              <div className="text-fg_primary text-l">{condition.value}</div>
            </div>
          </div>
        ))}
      </div>

      {sanitizedConditions && (
        <p className="text-fg_secondary text-l" dangerouslySetInnerHTML={{ __html: sanitizedConditions }} />
      )}
    </CardWrapper>
  );
};

type BusinessAddressProps = {
  deal?: DealDetailItem | null;
  loading?: boolean;
};

const propertyLinkItemClassNames = (type: 'link' | 'default') => {
  const base = 'gap-sm flex items-center h-[56px]';

  return classnames(
    base,
    type === 'link' && 'text-fg_link hover:text-fg_link_hover active:text-fg_link_pressed',
    type === 'default' && 'text-fg_secondary',
  );
};

export const BusinessAddress = ({ deal, loading }: BusinessAddressProps) => {
  if (loading || !deal) {
    return (
      <CardWrapper>
        <div className="bg-bg_secondary h-6 w-32 animate-pulse rounded" />
        <div className="gap-xs flex items-center">
          <div className="bg-bg_secondary h-5 w-5 animate-pulse rounded" />
          <div className="gap-xs flex flex-col">
            <div className="bg-bg_secondary h-4 w-36 animate-pulse rounded" />
            <div className="bg-bg_secondary h-4 w-28 animate-pulse rounded" />
          </div>
        </div>
        <div className="bg-bg_secondary h-[250px] w-full animate-pulse rounded-lg" />
      </CardWrapper>
    );
  }

  const { street, city } = deal?.place?.address || {};

  if (!street && !city) return null;

  const addressParts = [street, city].filter(Boolean).join(', ');
  const mapUrl = `https://maps.google.com/?q=${encodeURIComponent(addressParts)}`;

  return (
    <CardWrapper>
      <h2 className={classnames(cardHeaderClassNames)}>{_t(`${baseTranslationKey}.BusinessAddressSection.title`)}</h2>
      <ul>
        {street && (
          <li>
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={classnames(propertyLinkItemClassNames('link'), 'border-border_regular border-b')}>
              <Icon variant="shop" color="fg_secondary" />
              <span className="text-l underline">{street}</span>
            </a>
          </li>
        )}
        {city && (
          <li>
            <span className={propertyLinkItemClassNames('default')}>
              <Icon variant="location" size="sm" color="fg_secondary" />
              <span>{city}</span>
            </span>
          </li>
        )}
      </ul>
    </CardWrapper>
  );
};

type BrandProductCardProps = {
  deal: DealDetailItem | null;
  loading?: boolean;
};

export const BrandProductCard = ({ deal, loading }: BrandProductCardProps) => {
  if (loading) {
    return (
      <CardWrapper as="section">
        <div className="bg-bg_secondary aspect-video w-full animate-pulse rounded-lg" />
        <div className="bg-bg_secondary h-6 w-3/4 animate-pulse rounded" />
        <div className="bg-bg_secondary h-4 w-1/3 animate-pulse rounded" />
        <div className="space-y-sm">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-bg_secondary h-4 w-full animate-pulse rounded" />
          ))}
        </div>
      </CardWrapper>
    );
  }

  const brand = deal?.brand;

  if (!brand) return null;

  return (
    <CardWrapper as="section" className="fle">
      {brand.mainImageUrl && (
        <img
          src={getImagePreview(brand.mainImageUrl, 'big', (brand.title ?? '').toLowerCase().replace(/\s+/g, '-'))}
          alt={brand.title ?? ''}
          className="aspect-[552/267] w-full rounded-lg object-cover"
          loading="lazy"
        />
      )}

      <div className="space-y-sm">
        {brand.title && <h2 className="text-fg_primary text-h-m">{brand.title}</h2>}

        {(brand.priceLabel || brand.originalPriceLabel) && (
          <div className="gap-2xs flex items-center">
            {brand.priceLabel && <span className="text-fg_highlight text-xl">{brand.priceLabel}</span>}
            {brand.originalPriceLabel && (
              <span className="text-fg_secondary text-xl line-through">{brand.originalPriceLabel}</span>
            )}
          </div>
        )}
      </div>

      {brand.description && <p className="text-fg_secondary text-l">{brand.description}</p>}

      {brand.bullets && brand.bullets.length > 0 && (
        <ul className="space-y-sm">
          {brand.bullets.map((bullet, i) => (
            <li key={i} className="gap-md flex items-center">
              <StatusIcon type="success" className="bg-bg_primary" />
              <span className="text-fg_primary text-l">{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {brand.readMoreUrl && (
        <div className="">
          <LinkButton
            href={brand.readMoreUrl}
            target="_blank"
            rightIcon={<Icon variant="external-link" color="fg_link" size="sm" />}
            rel="noopener noreferrer">
            {_t('features.deals.pages.DealDetails.components.BrandProductCard.readMore')}
          </LinkButton>
        </div>
      )}
    </CardWrapper>
  );
};

const durationMS = 125;

const styles = `
.slide-enter { transform: translateY(100%) }
.slide-enter-active { transform: translateY(0); transition: all ${durationMS}ms linear }
.slide-exit { transform: translateY(0) }
.slide-exit-active { transform: translateY(100%); transition: transform ${durationMS}ms linear }
`;

export const FloatingCTAWrapper = ({ show, children }: { show: boolean; children: ReactNode }) => {
  return (
    <>
      <style>{styles}</style>
      <CSSTransition
        in={show}
        timeout={durationMS}
        classNames={{
          enter: 'slide-enter',
          enterActive: 'slide-enter-active',
          exit: 'slide-exit',
          exitActive: 'slide-exit-active',
        }}
        unmountOnExit>
        <div className="shadow-bottom-sm fixed bottom-0 left-0 right-0 z-10 bg-white">
          <div className={classnames('py-lg px-xl space-x-md container flex flex-row items-start justify-between')}>
            {children}
          </div>
        </div>
      </CSSTransition>
    </>
  );
};
